import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"

import type { MutatorTx } from "../schema"
import type {
	CancelAiDraftArgs,
	CreateCompanyUpvoteArgs,
	CreateThreadArgs,
	DeleteCompanyUpvoteArgs,
	DeleteThreadArgs,
	DeleteThreadBulkArgs,
	MarkEscalationReviewedArgs,
	MarkThreadSpamArgs,
	RegenerateAiDraftArgs,
	RestoreThreadArgs,
	SetThreadsReadArgs,
	UpdateThreadArgs,
	UpdateThreadBulkArgs,
} from "../types"
import { findThreadSender } from "./blocked-senders"

export const threadMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateThreadArgs
			ctx: ZeroJwtPayload
		}) => {
			await tx.mutate.feedbacks.insert({
				id: args.id,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				assigneeId: args.assigneeId || null,
				painLevel: args.painLevel || "UNKNOWN",
				text: args.text || "",
				reporterId: args.reporterId || null,
				state: args.state || "NEW",
				title: "New thread",
				origin: args.origin,
				lastInboundMessageAt: Date.now(),
				lastOutboundMessageAt: Date.now(),
				lastStateChangeAt: Date.now(),
				lastMessageAt: Date.now(),
				isRead: false,
				chatThreadId: null,
				number: null,
			})

			// Handle tag relationships
			if (args.tagIds) {
				for (const tagId of args.tagIds) {
					await tx.mutate.feedbackToTags.insert({
						A: args.id,
						B: tagId,
					})
				}
			}

			// Handle project relationships
			if (args.linkedProjectIds) {
				for (const projectId of args.linkedProjectIds) {
					await tx.mutate.feedbackToProjects.insert({
						A: args.id,
						B: projectId,
					})
				}
			}
		},
	),

	update: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: UpdateThreadArgs
			ctx: ZeroJwtPayload
		}) => {
			// Get existing thread
			const [existing] = await tx.query.feedbacks
				.where("id", "=", args.id)
				.where("workspaceId", "=", authData.workspaceId)
				.run()

			if (!existing) {
				throw new Error("Thread not found")
			}

			// Convert ISO date strings to timestamps if provided
			const snoozedUntil = args.snoozedUntilIso
				? new Date(args.snoozedUntilIso).getTime()
				: args.snoozedUntilIso === null
					? null
					: undefined

			const lastStateChangeAt = args.lastStateChangeAtIso
				? new Date(args.lastStateChangeAtIso).getTime()
				: args.lastStateChangeAtIso === null
					? null
					: undefined

			const slaDeadlineAt = args.slaDeadlineAtIso
				? new Date(args.slaDeadlineAtIso).getTime()
				: args.slaDeadlineAtIso === null
					? null
					: undefined

			// Handle company assignment logic
			// Note: Cascade to contact's other threads is handled by Inngest background job
			// in the server mutator to avoid transaction timeout
			const contactId = args.contactId
			let companyId = args.companyId
			if (args.companyId) {
				// Verify company exists and is not deleted
				const [company] = await tx.query.companies
					.where("id", "=", args.companyId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!company) {
					if (contactId === undefined) throw new Error("Company not found")
					companyId = null
				}
			}

			// Handle contact assignment logic
			// Note: Cascade to contact's other threads is handled by Inngest background job
			// in the server mutator to avoid transaction timeout
			if (contactId) {
				// Verify contact exists and is not deleted
				const [contact] = await tx.query.contacts
					.where("id", "=", contactId)
					.where("workspaceId", "=", authData.workspaceId)
					.where("isDeleted", "=", false)
					.run()

				if (!contact) {
					throw new Error("Contact not found")
				}
			}

			// Update thread with all fields
			await tx.mutate.feedbacks.update({
				id: args.id,
				assigneeId: args.assigneeId,
				companyId,
				contactId: contactId,
				painLevel: args.painLevel,
				isRead: args.isRead,
				state: args.state,
				text: args.text,
				title: args.title,
				snoozedUntil: snoozedUntil,
				lastStateChangeAt: lastStateChangeAt,
				slaDeadlineAt: slaDeadlineAt,
			})

			if (
				args.assigneeActivityId &&
				args.assigneeId !== undefined &&
				args.assigneeId !== (existing.assigneeId ?? null)
			) {
				const now = Date.now()
				const selfAssigned = args.assigneeId === authData.userId
				await tx.mutate.threadActivities.insert({
					id: args.assigneeActivityId,
					createdAt: now,
					updatedAt: now,
					version: 1,
					isDeleted: false,
					eventType: selfAssigned ? "self_assigned" : "assignee_changed",
					payload: selfAssigned
						? {
								assigneeId: authData.userId,
								oldAssigneeId: existing.assigneeId ?? null,
							}
						: {
								oldAssigneeId: existing.assigneeId ?? null,
								newAssigneeId: args.assigneeId,
							},
					workspaceId: authData.workspaceId,
					threadId: args.id,
					actorId: authData.userId,
				})
			}
		},
	),
	cancelAiDraft: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: CancelAiDraftArgs }) => {
			// Clear every AI draft column so:
			//  (a) any in-flight generation is aborted (`aiDraftStartedAt` reset)
			//  (b) any persisted draft is discarded (`aiDraftHtml` /
			//      `aiDraftGeneratedAt` / `aiDraftSources` reset), which lets the
			//      composer hide the badge as soon as the user starts editing.
			//  (c) any prior failure indicator is cleared (`aiDraftErrorAt`),
			//      so the agent's edit also dismisses the error pill.
			await tx.mutate.feedbacks.update({
				id: args.threadId,
				aiDraftStartedAt: null,
				aiDraftHtml: null,
				aiDraftGeneratedAt: null,
				aiDraftErrorAt: null,
				aiDraftSources: null,
			})
		},
	),
	markEscalationReviewed: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: MarkEscalationReviewedArgs
		}) => {
			await tx.mutate.feedbacks.update({
				id: args.id,
				agentEscalationReviewedAt: args.reviewed === false ? null : Date.now(),
			})
		},
	),
	markSpam: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: MarkThreadSpamArgs
			ctx: ZeroJwtPayload
		}) => {
			const sender = await findThreadSender(tx, authData.workspaceId, args.id)
			if (!sender.isBlocked) {
				await tx.mutate.blockedSender.insert({
					id: args.blockedSenderId,
					createdAt: Date.now(),
					workspaceId: authData.workspaceId,
					value: sender.email,
					type: "EMAIL",
				})
			}
			await tx.mutate.feedbacks.update({
				id: args.id,
				state: "PROCESSED",
				lastStateChangeAt: new Date(args.lastStateChangeAtIso).getTime(),
			})
		},
	),
	regenerateAiDraft: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: RegenerateAiDraftArgs }) => {
			// Optimistic in-flight state: the badge flips to "Drafting…" on the
			// same frame the user asks for a new draft.
			await tx.mutate.feedbacks.update({
				id: args.threadId,
				aiDraftStartedAt: new Date(args.startedAtIso).getTime(),
				aiDraftHtml: null,
				aiDraftGeneratedAt: null,
				aiDraftErrorAt: null,
				aiDraftSources: null,
			})
		},
	),
	delete: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: DeleteThreadArgs }) => {
			// Soft delete by setting isDeleted: true
			await tx.mutate.feedbacks.update({
				id: args.id,
				isDeleted: true,
			})
		},
	),
	restore: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: RestoreThreadArgs }) => {
			await tx.mutate.feedbacks.update({
				id: args.id,
				isDeleted: false,
			})
		},
	),
	createCompanyUpvote: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateCompanyUpvoteArgs
			ctx: ZeroJwtPayload
		}) => {
			const [company] = await tx.query.companies
				.where("id", "=", args.companyId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			if (!company) {
				throw new Error("Company not found")
			}

			// Attach to a company contact so the item surfaces in the (contact-scoped)
			// support portal view, which filters requests by the company's contacts.
			const [contact] = await tx.query.contacts
				.where("companyId", "=", args.companyId)
				.where("workspaceId", "=", authData.workspaceId)
				.where("isDeleted", "=", false)
				.run()

			await tx.mutate.feedbacks.insert({
				id: args.id,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				version: 1,
				workspaceId: authData.workspaceId,
				companyId: args.companyId,
				contactId: contact?.id ?? null,
				assigneeId: null,
				reporterId: null,
				painLevel: "UNKNOWN",
				text: "",
				state: "PROCESSED",
				title: "Customer Need",
				origin: "UPVOTE",
				lastInboundMessageAt: Date.now(),
				lastOutboundMessageAt: Date.now(),
				lastStateChangeAt: Date.now(),
				lastMessageAt: Date.now(),
				isRead: true,
				chatThreadId: null,
			})

			if (args.projectId) {
				await tx.mutate.feedbackToProjects.insert({
					A: args.id,
					B: args.projectId,
				})
			} else if (args.issueId) {
				await tx.mutate.feedbackToIssues.insert({
					A: args.id,
					B: args.issueId,
				})
			}
		},
	),
	deleteCompanyUpvote: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: DeleteCompanyUpvoteArgs
			ctx: ZeroJwtPayload
		}) => {
			const links = args.projectId
				? await tx.query.feedbackToProjects
						.where("B", "=", args.projectId)
						.run()
				: args.issueId
					? await tx.query.feedbackToIssues.where("B", "=", args.issueId).run()
					: []

			const feedbackIds = links.map((l) => l.A)
			if (feedbackIds.length === 0) {
				return
			}

			const matches = await tx.query.feedbacks
				.where("id", "IN", feedbackIds)
				.where("workspaceId", "=", authData.workspaceId)
				.where("companyId", "=", args.companyId)
				.where("origin", "=", "UPVOTE")
				.where("isDeleted", "=", false)
				.run()

			for (const match of matches) {
				await tx.mutate.feedbacks.update({ id: match.id, isDeleted: true })
			}
		},
	),
	updateBulk: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateThreadBulkArgs }) => {
			// Convert ISO date string to timestamp if provided
			const lastStateChangeAt = args.lastStateChangeAtIso
				? new Date(args.lastStateChangeAtIso).getTime()
				: undefined

			const snoozedUntil = args.snoozedUntilIso
				? new Date(args.snoozedUntilIso).getTime()
				: args.snoozedUntilIso === null
					? null
					: undefined

			// Update each thread individually since Zero doesn't support bulk updates
			for (const id of args.ids) {
				await tx.mutate.feedbacks.update({
					id: id,
					state: args.state,
					lastStateChangeAt: lastStateChangeAt,
					snoozedUntil: snoozedUntil,
				})
			}
		},
	),
	setReadBulk: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: SetThreadsReadArgs }) => {
			for (const id of args.ids) {
				await tx.mutate.feedbacks.update({ id, isRead: args.isRead })
			}
		},
	),
	deleteBulk: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: DeleteThreadBulkArgs }) => {
			// Soft delete each thread individually
			for (const id of args.ids) {
				await tx.mutate.feedbacks.update({
					id: id,
					isDeleted: true,
				})
			}
		},
	),
}
